/**
 * UI DENSITY, PART 2 OF 2 — the runtime half.
 *
 * Build-time (vite/px-to-rem.ts) turns every px length in the generated CSS
 * into rem. This module then drives the ROOT FONT-SIZE, which scales the whole
 * app at once. The point: a 1366x768 laptop should show roughly what a 16"
 * MacBook shows, instead of a UI that feels blown-up because every size was
 * authored against a big screen.
 *
 * WHY NOT `zoom` — measured, twice. CSS `zoom` on an ancestor makes
 * getBoundingClientRect() return zoom-scaled lengths that Floating UI
 * double-applies, so every Radix dropdown/select/popover/tooltip drifts off
 * its trigger (floating-ui #3032). Re-tested 2026-08-15 on @floating-ui/dom
 * 1.7.5 + current Chrome: 20/20 popovers still misplaced, up to 160px off at
 * zoom 0.8. rem is real layout, so measurement stays honest and Radix is
 * unaffected. Do not reintroduce `zoom` here.
 */

export type DensityMode = "auto" | "compact" | "comfortable";

export const DENSITY_VIEW_KEY = "uiDensity";

/** rem basis. Pinned, never inherited — see setRootScale(). */
const BASE_FONT_PX = 16;

/**
 * How far we may shrink is a READABILITY question, and readability depends on
 * the physical pixel, not the CSS pixel — so the floor is derived from
 * devicePixelRatio rather than being a flat number.
 *
 * `scale x dpr` is how big text ends up PHYSICALLY, relative to unscaled text
 * on an ordinary 100%-scaled display. Windows laptops differ enormously here:
 *
 *   1366x768 @100%  -> dpr 1.0  : nothing is magnifying, so 0.75 is the floor
 *   1920x1080 @125% -> dpr 1.25 : OS already magnifies, we can go deeper
 *   1920x1080 @150% -> dpr 1.5  : at 0.66 text is still ~1.0x physically
 *
 * The 150% machines are the ones with the least usable space (1280x720 CSS)
 * AND the most headroom to shrink — a flat floor would have penalised exactly
 * the users who needed the most help.
 */
/** Smallest physical text factor we will render, relative to a 1x display. */
const MIN_PHYSICAL_FACTOR = 0.75;
/** Hard stop regardless of dpr — below this the UI stops being comfortable. */
const ABS_MIN_SCALE = 0.62;
/** Never make the floor looser than this, even on a sub-1x display. */
const MAX_FLOOR = 0.8;

/** The scale floor for a given display. Exported for the Settings copy/tests. */
export function minScaleFor(dpr?: number): number {
  const d =
    dpr ?? (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1);
  return Math.min(MAX_FLOOR, Math.max(ABS_MIN_SCALE, MIN_PHYSICAL_FACTOR / d));
}

/**
 * Reference viewport the UI was authored against (16" MacBook, browser chrome
 * removed). Auto scale is how much of this a screen can actually show.
 */
const REF_W = 1600;
const REF_H = 950;

/**
 * Below this width the app is already in its tablet/mobile layouts, which are
 * designed for small screens. Shrinking those too would make a phone unusable,
 * so density is desktop-only.
 */
const DESKTOP_MIN_WIDTH = 1024;

/**
 * DEFAULT IS "comfortable" — full size, byte-identical to the app before the
 * density feature existed. "auto" shipped as the default first and small-laptop
 * users read the shrink as "everything got small"; scaling down is now strictly
 * opt-in via Settings → Manage → Display Density. Keep index.html's inline
 * first-paint script in sync with this default.
 */
let currentMode: DensityMode = "comfortable";
let rafId = 0;

/** Pure — exported for tests. */
export function computeScale(
  mode: DensityMode,
  width: number,
  height: number,
  dpr?: number,
): number {
  if (mode === "comfortable") return 1;
  // Phones and tablets keep their own purpose-built layouts untouched.
  if (width < DESKTOP_MIN_WIDTH) return 1;
  const floor = minScaleFor(dpr);
  if (mode === "compact") return floor;
  // Auto: fit to whichever axis is tighter, never scale UP past the reference.
  const fit = Math.min(width / REF_W, height / REF_H);
  return Math.min(1, Math.max(floor, fit));
}

function setRootScale(scale: number) {
  const el = document.documentElement;
  /**
   * ALWAYS write an explicit px value — never clear it to inherit the browser's
   * default font size. Before px->rem the app was pure px, so a user who had
   * bumped their browser font to 20px saw no difference. Leaving this unset
   * would silently scale the entire app by their browser setting, which reads
   * as "the app is broken". Pinning keeps scale=1 pixel-identical to before.
   */
  el.style.fontSize = `${BASE_FONT_PX * scale}px`;
  // Exposed for anything that must scale outside the rem system.
  el.style.setProperty("--ui-scale", String(scale));
  el.dataset.uiScale = scale === 1 ? "1" : scale.toFixed(3);
}

/** Recompute from the live viewport and apply. */
export function applyDensity() {
  const el = document.documentElement;
  setRootScale(computeScale(currentMode, el.clientWidth, el.clientHeight));
}

function onResize() {
  if (rafId) return;
  rafId = requestAnimationFrame(() => {
    rafId = 0;
    applyDensity();
  });
}

/**
 * Switch mode (from the user's DB-backed preference, or the Settings control)
 * and re-apply immediately.
 */
export function setDensityMode(mode: DensityMode) {
  if (mode === currentMode) return;
  currentMode = mode;
  applyDensity();
}

export function getDensityMode(): DensityMode {
  return currentMode;
}

/**
 * Boot. Called from main.tsx before React renders. index.html already pinned
 * the root font-size at 16px inline (the "comfortable" default); this takes
 * over and adds the resize listener for users whose saved mode scales.
 */
export function initUiDensity() {
  if (typeof window === "undefined") return;
  applyDensity();
  window.addEventListener("resize", onResize, { passive: true });
}
